import { getToken } from '@optimus-web/common';
import { DENSE_LIGHT_THEME } from '@optimus-web/theme';
import { EnhancedResource, ResourceUsePlaceholder } from '@mews-commander/legacy-entities/src/ducks/resourcesTree';
import { LINE_HEIGHT, CORNER_RADIUS, LABEL_FONT_SIZE, DATE_LEGEND_ROW_COUNTS } from '../../constants';
import { drawText, drawUseRectangle, fitText } from '../../utils/drawUtils';
import { TimelineModel, TimelineState } from '../../types';
import { NewReservationInfo } from '../../../newReservation/components/NewReservationPanel/types';
import { TimelineService } from '../../timelineService';

export default function ConfirmedResourceUsePlaceholder() {
    return {
        render(state: TimelineState, model: TimelineModel) {
            drawUsePlaceholder(state, model);
        },
    };

    function drawUsePlaceholder(state: TimelineState, model: TimelineModel) {
        const { ctx, scale, viewport, constXAxis, constYAxis } = state;
        const topSpacing = 3.5 * scale;
        const bottomSpacing = 2.5 * scale;
        const lineHeight = LINE_HEIGHT * scale;
        const fontSize = LABEL_FONT_SIZE * scale;

        const p = model.getConfirmedResourceUsePlaceholder();
        const resource = p ? model.findResourceById(p.resourceId) : undefined;

        if (!resource || !p) {
            return;
        }

        const placeholder = newReservationInfoToResourceUsePlaceholder(p, state, resource);
        const color = getToken('borderInteractiveBoldActive')({ theme: DENSE_LIGHT_THEME });

        const resourceOffset = constYAxis((resource.minY - viewport.minY) * (1 - scale)) as number;
        const scrollOffsetX = constXAxis(viewport.minX) as number;
        const scrollOffsetY = constYAxis(viewport.minY) as number;

        const x1 = placeholder.x1 - scrollOffsetX;
        const x2 = placeholder.x2 - scrollOffsetX;
        const y1 = placeholder.y1 + topSpacing - scrollOffsetY - resourceOffset;

        drawUseRectangle({
            ctx,
            x1,
            x2,
            y1,
            y2: placeholder.y1 + lineHeight - bottomSpacing - scrollOffsetY - resourceOffset,
            radius: CORNER_RADIUS / scale,
            bgColor: getToken('backgroundStaticFlat')({ theme: DENSE_LIGHT_THEME }),
            strokeColor: color,
            dashed: true,
            bgAlpha: 0.4,
        });

        const textPadding = 8 * scale;
        const fit = fitText(ctx, state.newReservationPlaceholderLabel, x2 - x1 - 2 * textPadding);
        const label =
            fit.text !== state.newReservationPlaceholderLabel
                ? `${fit.text.substring(0, fit.text.length - 2)}...`
                : state.newReservationPlaceholderLabel;

        drawText(
            ctx,
            label,
            x1 + textPadding,
            y1 + fontSize / 2 + lineHeight / 2 - topSpacing - bottomSpacing,
            false,
            fontSize,
            color
        );
    }
}

function newReservationInfoToResourceUsePlaceholder(
    newReservationInfo: NewReservationInfo,
    state: TimelineState,
    resource: EnhancedResource
) {
    const x1 = TimelineService.utcToTimeUnitIndex(newReservationInfo.startUtc, state.timezone, state.timeUnit);
    const x2 = TimelineService.utcToTimeUnitIndex(newReservationInfo.endUtc, state.timezone, state.timeUnit);

    const placeholder: ResourceUsePlaceholder = {
        startUtc: newReservationInfo.startUtc,
        endUtc: newReservationInfo.endUtc,
        x1: state.constXAxis(x1) as number,
        x2: state.constXAxis(x2) as number,
        y1: state.constYAxis(resource.minY + DATE_LEGEND_ROW_COUNTS) as number,
        y2: state.constYAxis(resource.maxY + DATE_LEGEND_ROW_COUNTS) as number,
    };

    return placeholder;
}
